import React, { useState } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Palette, RotateCw } from "lucide-react";
import { absUrl } from "@/lib/api";

/**
 * ⚠️  FROZEN BETA v1 — DO NOT MODIFY.
 * First-cut cartoon garage scene, preserved for the admin-only playground at
 * /admin/play-garage. The live/iterating garage lives in GarageHero.js.
 *
 * Cartoon-illustrated garage bay — Forza-vignette vibes, not stick-figure.
 */
const PAINT_FILTERS = [
    { name: "F8 Black", filter: "none" },
    { name: "Hellcat Red", filter: "hue-rotate(0deg) saturate(1.8) brightness(0.9)" },
    { name: "B5 Blue", filter: "hue-rotate(200deg) saturate(1.4) brightness(0.85)" },
    { name: "Sublime Green", filter: "hue-rotate(85deg) saturate(1.7) brightness(0.9)" },
    { name: "Destroyer Grey", filter: "grayscale(0.7) brightness(0.85)" },
];

export default function GarageHeroV1({ featuredVehicle }) {
    const [lightsOn, setLightsOn] = useState(true);
    const [rotation, setRotation] = useState(0);
    const [paintIdx, setPaintIdx] = useState(0);

    const paint = PAINT_FILTERS[paintIdx];
    const carPhoto = featuredVehicle?.photos?.[0]
        ? absUrl(featuredVehicle.photos[0])
        : null;

    return (
        <div
            className="relative overflow-hidden"
            data-testid="garage-hero"
            style={{
                height: "clamp(280px, 46vh, 420px)",
                background: "#050505",
                borderBottom: "1px solid #262626",
            }}
        >
            {/* Full-scene SVG backdrop */}
            <svg
                viewBox="0 0 800 400"
                preserveAspectRatio="xMidYMid slice"
                className="absolute inset-0 w-full h-full"
                aria-hidden="true"
            >
                <defs>
                    {/* Back-wall / rollup door gradient */}
                    <linearGradient id="wall-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0f0f10" />
                        <stop offset="50%" stopColor="#1a1a1c" />
                        <stop offset="100%" stopColor="#0a0a0b" />
                    </linearGradient>
                    <linearGradient id="floor-grad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0d0d0d" />
                        <stop offset="60%" stopColor="#080808" />
                        <stop offset="100%" stopColor="#020202" />
                    </linearGradient>
                    {/* Tube light glow */}
                    <radialGradient id="tube-glow" cx="50%" cy="50%" r="60%">
                        <stop offset="0%" stopColor="#fff8dc" stopOpacity="0.85" />
                        <stop offset="50%" stopColor="#fff2b8" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#fff2b8" stopOpacity="0" />
                    </radialGradient>
                    {/* Red LED strip */}
                    <linearGradient id="led-strip" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#E10600" stopOpacity="0" />
                        <stop offset="15%" stopColor="#ff5f56" stopOpacity="0.85" />
                        <stop offset="50%" stopColor="#ff8f8a" stopOpacity="1" />
                        <stop offset="85%" stopColor="#ff5f56" stopOpacity="0.85" />
                        <stop offset="100%" stopColor="#E10600" stopOpacity="0" />
                    </linearGradient>
                    {/* Toolbox red */}
                    <linearGradient id="tb-red" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#c31510" />
                        <stop offset="60%" stopColor="#8a0300" />
                        <stop offset="100%" stopColor="#3a0100" />
                    </linearGradient>
                    <linearGradient id="tb-panel" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a41008" />
                        <stop offset="100%" stopColor="#5a0300" />
                    </linearGradient>
                </defs>

                {/* Back wall base */}
                <rect x="0" y="0" width="800" height="290" fill="url(#wall-grad)" />

                {/* Rollup garage door — segmented panels centered */}
                <g opacity={lightsOn ? 1 : 0.55}>
                    <rect
                        x="180"
                        y="30"
                        width="440"
                        height="240"
                        fill="#141416"
                        stroke="#2a2a2c"
                        strokeWidth="1.5"
                    />
                    {/* Guide rails */}
                    <rect x="176" y="26" width="4" height="248" fill="#0a0a0b" />
                    <rect x="620" y="26" width="4" height="248" fill="#0a0a0b" />
                    {/* Segment lines */}
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                        <line
                            key={`panel-${i}`}
                            x1="180"
                            y1={30 + i * 34}
                            x2="620"
                            y2={30 + i * 34}
                            stroke="#0a0a0b"
                            strokeWidth="1.5"
                        />
                    ))}
                    {/* Segment shadow line under each panel edge */}
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                        <line
                            key={`panel-sh-${i}`}
                            x1="180"
                            y1={30 + i * 34 + 1}
                            x2="620"
                            y2={30 + i * 34 + 1}
                            stroke="#26262a"
                            strokeWidth="0.7"
                            opacity="0.7"
                        />
                    ))}
                    {/* Small viewing windows in top panel */}
                    <g opacity="0.65">
                        {[0, 1, 2, 3].map((i) => (
                            <rect
                                key={`window-${i}`}
                                x={220 + i * 100}
                                y="42"
                                width="70"
                                height="18"
                                fill="#1a1c22"
                                stroke="#2f3238"
                                strokeWidth="1"
                            />
                        ))}
                    </g>
                    {/* Door handle */}
                    <rect x="392" y="252" width="16" height="8" fill="#3a3d42" />
                </g>

                {/* SRT poster on back wall (right of door) */}
                <g opacity={lightsOn ? 0.9 : 0.4} transform="translate(660,60)">
                    <rect
                        width="90"
                        height="120"
                        fill="#0a0a0b"
                        stroke="#2a2a2c"
                        strokeWidth="1"
                    />
                    <text
                        x="45"
                        y="52"
                        textAnchor="middle"
                        fontFamily="Alfa Slab One, serif"
                        fontSize="30"
                        fill="#E10600"
                        opacity="0.95"
                    >
                        SRT
                    </text>
                    <text
                        x="45"
                        y="82"
                        textAnchor="middle"
                        fontFamily="Alfa Slab One, serif"
                        fontSize="12"
                        fill="#eef1f5"
                        opacity="0.85"
                    >
                        HELLCAT
                    </text>
                    <line x1="10" y1="98" x2="80" y2="98" stroke="#3a3d42" strokeWidth="1" />
                    <line x1="18" y1="106" x2="72" y2="106" stroke="#3a3d42" strokeWidth="0.6" />
                </g>

                {/* PEGBOARD w/ tools — left wall */}
                <g opacity={lightsOn ? 0.95 : 0.55} transform="translate(30,50)">
                    {/* Pegboard base */}
                    <rect
                        width="130"
                        height="180"
                        fill="#141216"
                        stroke="#2a2a2c"
                        strokeWidth="1"
                    />
                    {/* Peg dots grid */}
                    {Array.from({ length: 9 }).map((_, r) =>
                        Array.from({ length: 6 }).map((_, c) => (
                            <circle
                                key={`peg-${r}-${c}`}
                                cx={12 + c * 22}
                                cy={12 + r * 20}
                                r="0.9"
                                fill="#2a2a2c"
                            />
                        ))
                    )}
                    {/* Wrench */}
                    <g stroke="#3a3d42" strokeWidth="1.2" fill="#c9ced4" transform="translate(10,22)">
                        <path d="M 0 6 L 40 6 L 44 2 L 52 2 L 52 10 L 44 10 L 40 6 Z" />
                        <circle cx="6" cy="6" r="4" fill="#141416" stroke="#c9ced4" strokeWidth="1.4" />
                    </g>
                    {/* Ratchet */}
                    <g fill="#8b929c" stroke="#3a3d42" strokeWidth="1" transform="translate(74,20)">
                        <rect x="0" y="4" width="34" height="4" rx="1" />
                        <circle cx="34" cy="6" r="6" fill="#a9b0b8" stroke="#3a3d42" />
                        <circle cx="34" cy="6" r="2.5" fill="#141416" />
                    </g>
                    {/* Hammer */}
                    <g fill="#8b929c" stroke="#2a2a2c" strokeWidth="1" transform="translate(14,60)">
                        <rect x="0" y="8" width="26" height="3" fill="#6a4a2a" />
                        <path d="M 24 4 L 40 4 L 44 8 L 44 12 L 40 16 L 24 16 Z" fill="#4a5058" />
                    </g>
                    {/* Screwdriver */}
                    <g transform="translate(78,66)">
                        <rect x="0" y="0" width="4" height="16" fill="#E10600" rx="1" />
                        <rect x="4" y="6" width="22" height="4" fill="#c9ced4" />
                        <path d="M 26 5 L 30 8 L 26 11 Z" fill="#8b929c" />
                    </g>
                    {/* Socket wrench set — small circles */}
                    <g transform="translate(20,110)">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <g key={`sock-${i}`}>
                                <circle cx={i * 15} cy="6" r="6" fill="#4a5058" stroke="#2a2a2c" />
                                <circle cx={i * 15} cy="6" r="2" fill="#0a0a0b" />
                            </g>
                        ))}
                    </g>
                    {/* Work light */}
                    <g transform="translate(76,110)">
                        <circle cx="14" cy="10" r="10" fill="#f0d78a" stroke="#3a3d42" strokeWidth="1.2" />
                        <circle cx="14" cy="10" r="5" fill="#ffedb0" opacity={lightsOn ? 0.9 : 0.25} />
                        <rect x="12" y="19" width="4" height="8" fill="#2a2a2c" />
                    </g>
                    {/* Zip ties / straps hanging */}
                    <g stroke="#2a2a2c" strokeWidth="1" fill="none" transform="translate(14,142)">
                        {[0, 1, 2, 3].map((i) => (
                            <path
                                key={`hang-${i}`}
                                d={`M ${i * 26 + 4} 0 L ${i * 26 + 4} 32`}
                                stroke="#3a3d42"
                                strokeWidth="0.8"
                            />
                        ))}
                    </g>
                </g>

                {/* TIRE STACK — right side */}
                <g transform="translate(650,180)" opacity={lightsOn ? 0.95 : 0.6}>
                    {[0, 1, 2].map((i) => (
                        <g key={`tire-${i}`} transform={`translate(0,${-i * 18})`}>
                            <ellipse cx="40" cy="24" rx="42" ry="14" fill="#050505" />
                            <ellipse cx="40" cy="22" rx="42" ry="14" fill="#0a0a0a" stroke="#1a1a1a" strokeWidth="1" />
                            {/* Rim inner */}
                            <ellipse cx="40" cy="22" rx="20" ry="6" fill="#141416" stroke="#3a3d42" strokeWidth="0.8" />
                            <ellipse cx="40" cy="22" rx="6" ry="1.6" fill="#5a5f66" />
                        </g>
                    ))}
                </g>

                {/* ROLLING TOOL CHEST — bottom-left, red Snap-On vibe */}
                <g transform="translate(50,240)">
                    {/* Top box */}
                    <rect x="0" y="0" width="130" height="34" fill="url(#tb-red)" stroke="#2a0500" strokeWidth="1.5" />
                    {/* Top handle */}
                    <rect x="45" y="-4" width="40" height="4" fill="#c9ced4" />
                    {/* Top drawers */}
                    {[0, 1].map((i) => (
                        <g key={`td-${i}`}>
                            <rect x="4" y={4 + i * 14} width="122" height="12" fill="url(#tb-panel)" stroke="#3a0100" />
                            <rect x="58" y={9 + i * 14} width="14" height="3" fill="#3a0100" />
                        </g>
                    ))}
                    {/* Middle body */}
                    <rect x="0" y="34" width="130" height="86" fill="url(#tb-red)" stroke="#2a0500" strokeWidth="1.5" />
                    {/* Big drawers */}
                    {[0, 1, 2, 3].map((i) => (
                        <g key={`bd-${i}`}>
                            <rect x="4" y={38 + i * 20} width="122" height="18" fill="url(#tb-panel)" stroke="#3a0100" />
                            <rect x="58" y={45 + i * 20} width="14" height="4" fill="#3a0100" />
                        </g>
                    ))}
                    {/* Casters */}
                    {[8, 118].map((x) => (
                        <g key={`cast-${x}`}>
                            <rect x={x - 2} y="120" width="8" height="6" fill="#3a3d42" />
                            <circle cx={x + 2} cy="128" r="4" fill="#050505" stroke="#3a3d42" strokeWidth="0.8" />
                        </g>
                    ))}
                </g>

                {/* JERRY CAN + OIL DRUM — bottom-right corner */}
                <g transform="translate(690,270)" opacity={lightsOn ? 1 : 0.65}>
                    {/* Oil drum */}
                    <rect x="0" y="10" width="46" height="60" fill="#2a2a2c" stroke="#0a0a0b" strokeWidth="1.5" />
                    <ellipse cx="23" cy="10" rx="23" ry="4" fill="#3a3d42" stroke="#0a0a0b" />
                    <rect x="0" y="30" width="46" height="3" fill="#0a0a0b" />
                    <rect x="0" y="50" width="46" height="3" fill="#0a0a0b" />
                    <text
                        x="23"
                        y="46"
                        textAnchor="middle"
                        fontFamily="JetBrains Mono, monospace"
                        fontSize="7"
                        fill="#E10600"
                        opacity="0.85"
                    >
                        MOPAR
                    </text>
                    {/* Jerry can */}
                    <g transform="translate(50,32)">
                        <path
                            d="M 0 6 L 0 38 Q 0 42 4 42 L 34 42 Q 38 42 38 38 L 38 6 Q 38 2 34 2 L 4 2 Q 0 2 0 6 Z"
                            fill="#8a0300"
                            stroke="#3a0100"
                            strokeWidth="1.2"
                        />
                        <rect x="12" y="0" width="14" height="4" fill="#3a0100" />
                        <line x1="6" y1="10" x2="32" y2="10" stroke="#3a0100" strokeWidth="0.8" />
                        <line x1="6" y1="20" x2="32" y2="20" stroke="#3a0100" strokeWidth="0.8" />
                    </g>
                </g>

                {/* Floor — extends from y=290 down */}
                <rect x="0" y="290" width="800" height="110" fill="url(#floor-grad)" />
                {/* Floor tile grid — perspective-hinted */}
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <line
                        key={`fh-${i}`}
                        x1="0"
                        y1={300 + i * 20}
                        x2="800"
                        y2={300 + i * 20}
                        stroke="#141414"
                        strokeOpacity="0.6"
                        strokeWidth="0.6"
                    />
                ))}
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
                    const midX = 400;
                    const vanish = 260;
                    const x = 80 + i * 80;
                    return (
                        <line
                            key={`fv-${i}`}
                            x1={x}
                            y1="290"
                            x2={midX + (x - midX) * 2.2}
                            y2="400"
                            stroke="#141414"
                            strokeOpacity="0.5"
                            strokeWidth="0.6"
                        />
                    );
                })}
                {/* Oil stain center-back */}
                <ellipse
                    cx="400"
                    cy="308"
                    rx="90"
                    ry="10"
                    fill="#000"
                    opacity="0.55"
                />
                {/* Tire skid mark */}
                <path
                    d="M 240 340 Q 380 320 560 340"
                    stroke="#000"
                    strokeOpacity="0.5"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                />

                {/* Under-car red LED strip */}
                <rect
                    x="240"
                    y="358"
                    width="320"
                    height="3"
                    fill="url(#led-strip)"
                    opacity={lightsOn ? 1 : 0.15}
                />

                {/* OVERHEAD FLUORESCENT FIXTURES — twin-tube */}
                {[220, 580].map((cx, i) => (
                    <g key={`fx-${i}`} opacity={lightsOn ? 1 : 0.3}>
                        {/* Housing */}
                        <rect x={cx - 60} y="0" width="120" height="14" fill="#1a1a1c" stroke="#0a0a0b" strokeWidth="1" />
                        {/* Twin tubes */}
                        <rect x={cx - 56} y="4" width="112" height="2.5" fill="#fff8dc" opacity="0.95" />
                        <rect x={cx - 56} y="8.5" width="112" height="2.5" fill="#fff8dc" opacity="0.95" />
                        {/* Hanging chain */}
                        <line x1={cx - 40} y1="0" x2={cx - 40} y2="-24" stroke="#3a3d42" strokeWidth="0.6" strokeDasharray="1 1" />
                        <line x1={cx + 40} y1="0" x2={cx + 40} y2="-24" stroke="#3a3d42" strokeWidth="0.6" strokeDasharray="1 1" />
                    </g>
                ))}

                {/* Cone lights from fixtures */}
                {[220, 580].map((cx, i) => (
                    <path
                        key={`cone-${i}`}
                        d={`M ${cx - 60} 14 L ${cx - 180} 320 L ${cx + 180} 320 L ${cx + 60} 14 Z`}
                        fill="url(#tube-glow)"
                        opacity={lightsOn ? 0.28 : 0}
                    />
                ))}

                {/* Ceiling LED red strip — top edge */}
                <rect
                    x="0"
                    y="0"
                    width="800"
                    height="2"
                    fill="url(#led-strip)"
                    opacity={lightsOn ? 0.85 : 0.1}
                />
            </svg>

            {/* Pulse animation for the red LEDs (framer-motion overlay on top of SVG) */}
            <motion.div
                aria-hidden
                className="absolute left-0 right-0 top-0 h-[2px] pointer-events-none"
                animate={{ opacity: lightsOn ? [0.5, 1, 0.5] : 0.1 }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                style={{
                    background:
                        "linear-gradient(90deg, transparent 0%, #E10600 30%, #ff5f56 50%, #E10600 70%, transparent 100%)",
                    filter: "blur(1px)",
                }}
            />

            {/* CENTER CAR — sits above the SVG */}
            <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{ paddingBottom: 44 }}
            >
                <motion.div
                    className="relative"
                    animate={{ rotate: rotation, y: [0, -3, 0] }}
                    transition={{
                        rotate: { type: "spring", stiffness: 60, damping: 14 },
                        y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
                    }}
                    style={{ width: "min(60%, 480px)", filter: paint.filter }}
                >
                    {carPhoto ? (
                        <img
                            src={carPhoto}
                            alt={`${featuredVehicle.year} ${featuredVehicle.model_name}`}
                            className="w-full h-auto object-contain select-none"
                            draggable={false}
                            style={{
                                maxHeight: 220,
                                filter:
                                    "drop-shadow(0 22px 22px rgba(0,0,0,0.85)) drop-shadow(0 0 44px rgba(225,6,0,0.22))",
                            }}
                        />
                    ) : (
                        <StylizedCharger />
                    )}
                    {/* Contact shadow */}
                    <div
                        aria-hidden
                        className="absolute left-1/2 -translate-x-1/2 -bottom-2 pointer-events-none"
                        style={{
                            width: "78%",
                            height: 16,
                            background:
                                "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.85) 0%, transparent 70%)",
                            filter: "blur(6px)",
                        }}
                    />
                </motion.div>
            </div>

            {/* Interactive controls — bottom-right cluster */}
            <div
                className="absolute right-3 bottom-3 flex flex-col gap-2 items-end"
                data-testid="garage-controls"
            >
                <ControlBtn
                    label={lightsOn ? "LIGHTS ON" : "LIGHTS OFF"}
                    active={lightsOn}
                    icon={Lightbulb}
                    onClick={() => setLightsOn((v) => !v)}
                    testid="ctrl-lights"
                />
                <ControlBtn
                    label={`PAINT · ${paint.name}`}
                    active={paintIdx > 0}
                    icon={Palette}
                    onClick={() =>
                        setPaintIdx((i) => (i + 1) % PAINT_FILTERS.length)
                    }
                    testid="ctrl-paint"
                />
                <ControlBtn
                    label="ROTATE"
                    icon={RotateCw}
                    onClick={() => setRotation((r) => r - 12)}
                    testid="ctrl-rotate"
                />
            </div>
        </div>
    );
}

function ControlBtn({ label, active, icon: Icon, onClick, testid }) {
    return (
        <motion.button
            whileTap={{ scale: 0.94 }}
            whileHover={{ x: -2 }}
            onClick={onClick}
            data-testid={testid}
            className="flex items-center gap-2 px-3 py-2 text-[10px] font-bold text-white"
            style={{
                background: active
                    ? "linear-gradient(180deg, #ff3b33 0%, #E10600 55%, #7a0400 100%)"
                    : "rgba(10,10,10,0.75)",
                border: active ? "1px solid #ff8f8a" : "1px solid #262626",
                borderRadius: 4,
                backdropFilter: "blur(10px)",
                letterSpacing: "0.16em",
                boxShadow: active
                    ? "0 6px 18px rgba(225,6,0,0.4)"
                    : "0 4px 12px rgba(0,0,0,0.5)",
                fontFamily: "'JetBrains Mono', monospace",
            }}
        >
            <Icon className="w-3.5 h-3.5" strokeWidth={2.4} />
            {label}
        </motion.button>
    );
}

/**
 * Stylized Challenger silhouette — used when the user hasn't uploaded a car
 * photo yet. Cartoon/illustration style: bold linework, dramatic red rim light,
 * proper Widebody stance, 5-split-spoke wheels.
 */
function StylizedCharger() {
    return (
        <div className="relative flex items-center justify-center" style={{ height: 200 }}>
            <svg viewBox="0 0 520 200" className="w-full h-auto" aria-hidden>
                <defs>
                    <linearGradient id="body-side" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22262c" />
                        <stop offset="55%" stopColor="#0a0a0b" />
                        <stop offset="100%" stopColor="#000" />
                    </linearGradient>
                    <linearGradient id="body-rim-light" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ff5f56" stopOpacity="0" />
                        <stop offset="55%" stopColor="#ff5f56" stopOpacity="0.6" />
                        <stop offset="100%" stopColor="#ff5f56" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="glass" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3a3d42" />
                        <stop offset="60%" stopColor="#141416" />
                        <stop offset="100%" stopColor="#050506" />
                    </linearGradient>
                    <radialGradient id="hl" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#fff2b8" />
                        <stop offset="60%" stopColor="#d99a3a" />
                        <stop offset="100%" stopColor="#3a2200" />
                    </radialGradient>
                </defs>

                {/* Ground shadow */}
                <ellipse cx="260" cy="176" rx="220" ry="14" fill="#000" opacity="0.65" />

                {/* Body — Widebody Challenger side profile with fender flares */}
                <path
                    d="M 40 148
                       Q 42 122 68 116
                       L 92 108
                       Q 108 92 138 88
                       L 220 78
                       Q 260 74 306 78
                       L 380 90
                       Q 412 96 440 108
                       L 468 116
                       Q 488 122 490 148
                       L 470 152
                       Q 466 168 442 168
                       Q 418 168 414 152
                       L 116 152
                       Q 112 168 88 168
                       Q 64 168 60 152
                       Z"
                    fill="url(#body-side)"
                    stroke="#0a0a0b"
                    strokeWidth="1.4"
                />
                {/* Widebody fender flares */}
                <path
                    d="M 60 152 Q 88 130 116 152 Z"
                    fill="#050506"
                    stroke="#1a1a1a"
                    strokeWidth="0.6"
                />
                <path
                    d="M 414 152 Q 442 130 470 152 Z"
                    fill="#050506"
                    stroke="#1a1a1a"
                    strokeWidth="0.6"
                />
                {/* Roof edge highlight */}
                <path
                    d="M 138 88 Q 220 76 306 78 L 380 90"
                    stroke="#5a6068"
                    strokeWidth="1"
                    fill="none"
                    opacity="0.6"
                />

                {/* Greenhouse / windows */}
                <path
                    d="M 148 96 Q 220 84 302 88 L 372 100 L 348 116 Q 310 108 264 106 Q 220 108 168 116 Z"
                    fill="url(#glass)"
                    stroke="#2a2c30"
                    strokeWidth="0.8"
                />
                {/* B-pillar */}
                <line x1="252" y1="88" x2="252" y2="116" stroke="#0a0a0b" strokeWidth="2" />
                {/* Window reflection */}
                <path
                    d="M 168 100 L 224 92 L 236 100 L 190 108 Z"
                    fill="#c9ced4"
                    opacity="0.14"
                />

                {/* Rim red-lighting glow along the flank */}
                <path
                    d="M 60 130 L 470 130"
                    stroke="url(#body-rim-light)"
                    strokeWidth="18"
                    fill="none"
                    opacity="0.55"
                    strokeLinecap="round"
                />

                {/* Body character line */}
                <path
                    d="M 100 130 L 440 130"
                    stroke="#0a0a0b"
                    strokeWidth="1"
                    opacity="0.7"
                />

                {/* Door cutlines */}
                <path d="M 232 110 L 232 152" stroke="#0a0a0b" strokeWidth="0.9" />
                <path d="M 306 108 L 306 152" stroke="#0a0a0b" strokeWidth="0.9" />

                {/* SRT badge dot */}
                <circle cx="326" cy="128" r="2.6" fill="#E10600" />

                {/* Headlight */}
                <ellipse cx="66" cy="130" rx="8" ry="4" fill="url(#hl)" opacity="0.95" />
                {/* Headlight glow */}
                <ellipse cx="52" cy="130" rx="22" ry="6" fill="#ffdba0" opacity="0.35" />

                {/* Taillight */}
                <rect x="472" y="124" width="10" height="6" fill="#ff2a22" rx="1" />
                <rect x="470" y="126" width="14" height="2" fill="#ff8f8a" opacity="0.5" />

                {/* Front bumper splitter */}
                <path
                    d="M 40 148 L 44 158 L 76 158 L 78 148 Z"
                    fill="#050506"
                    stroke="#1a1a1a"
                    strokeWidth="0.5"
                />
                {/* Rear diffuser */}
                <path
                    d="M 452 148 L 454 158 L 486 158 L 490 148 Z"
                    fill="#050506"
                    stroke="#1a1a1a"
                    strokeWidth="0.5"
                />

                {/* WHEELS — 5-split-spoke Slingshot */}
                {[
                    { cx: 88, cy: 156 },
                    { cx: 442, cy: 156 },
                ].map((w, i) => (
                    <g key={`wheel-${i}`} transform={`translate(${w.cx},${w.cy})`}>
                        <circle r="26" fill="#050505" />
                        <circle r="24" fill="#080808" stroke="#161616" strokeWidth="0.5" />
                        {/* Rim lip */}
                        <circle r="19" fill="#1a1c20" stroke="#3a3d42" strokeWidth="0.6" />
                        {/* Face */}
                        <circle r="17" fill="#141618" />
                        {/* Split spokes */}
                        {[0, 72, 144, 216, 288].map((deg) => (
                            <g key={`sp-${deg}`} transform={`rotate(${deg})`}>
                                <path
                                    d="M -3 -16 L -1 -6 L -0.8 -3 L 0.8 -3 L 1 -6 L 3 -16 Q 0 -18 -3 -16 Z"
                                    fill="#3a3d42"
                                    stroke="#050506"
                                    strokeWidth="0.4"
                                />
                            </g>
                        ))}
                        {/* Center cap */}
                        <circle r="4" fill="#0a0a0b" stroke="#3a3d42" strokeWidth="0.6" />
                        <circle r="1.5" fill="#E10600" />
                    </g>
                ))}

                {/* Front lip red glow reflected on ground */}
                <ellipse cx="260" cy="176" rx="180" ry="4" fill="#E10600" opacity="0.15" />
            </svg>
            <span
                className="absolute -bottom-4 text-[10px] text-onyx-500"
                style={{
                    letterSpacing: "0.24em",
                    fontFamily: "'JetBrains Mono', monospace",
                }}
            >
                ADD YOUR FIRST RIDE
            </span>
        </div>
    );
}
